export const DEFAULT_TIME_ZONE = 'Europe/Amsterdam'
export const AUTOMATIC_TIME_ZONE = '__automatic__'

const TIME_ZONE_STORAGE_KEY = 'gmp.timeZoneId'

export const detectBrowserTimeZone = () => {
  if (!import.meta.client) return undefined

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const setTimeZonePreference = (timeZoneId?: string | null) => {
  if (!import.meta.client) return

  if (timeZoneId) localStorage.setItem(TIME_ZONE_STORAGE_KEY, timeZoneId)
  else localStorage.removeItem(TIME_ZONE_STORAGE_KEY)
}

export const getEffectiveTimeZone = () => {
  const preference = import.meta.client ? localStorage.getItem(TIME_ZONE_STORAGE_KEY) : null
  return preference || detectBrowserTimeZone() || DEFAULT_TIME_ZONE
}

export const getSupportedTimeZones = () => {
  const supportedValuesOf = (
    Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
  ).supportedValuesOf

  const zones = supportedValuesOf?.call(Intl, 'timeZone') ?? [DEFAULT_TIME_ZONE, 'UTC']
  return [DEFAULT_TIME_ZONE, ...zones.filter((zone) => zone !== DEFAULT_TIME_ZONE)]
}

export const formatTimeZoneLabel = (timeZoneId: string) => timeZoneId.replaceAll('_', ' ')

const hasExplicitOffset = (value: string) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)

/** Parses a field whose API contract is a UTC instant, including legacy suffixless values. */
export const parseUtcInstant = (value: string | Date) => {
  if (value instanceof Date) return value
  return new Date(hasExplicitOffset(value) ? value : `${value}Z`)
}

export const formatInstant = (
  value?: string | Date,
  locale = 'en-GB',
  options?: Intl.DateTimeFormatOptions & { timeZone?: string },
) => {
  if (!value) return '-'

  const date = parseUtcInstant(value)
  if (Number.isNaN(date.getTime())) return typeof value === 'string' ? value : '-'

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: options?.timeZone ?? getEffectiveTimeZone(),
    ...options,
  }).format(date)
}
